import {
  ApplicationRef,
  ComponentRef,
  createNgModule,
  Directive,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
  Type,
  ViewContainerRef
} from '@angular/core';
import { MmuRemoteComponentConfig } from '../interfaces/MmuRemoteComponentConfig';
import { MmuRemoteModuleService } from '../service/mmu-remote-module.service';


@Directive({
  selector: '[mmuRemoteComponent]'
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class MmuRemoteComponentRenderer implements OnInit {
  @Input()
  set mmuRemoteComponent(config: MmuRemoteComponentConfig) {
    this._config = config;
  }

  @Output() componentLoaded = new EventEmitter<ComponentRef<any>>();

  private _config!: MmuRemoteComponentConfig;
  private _componentInstance: ComponentRef<any> | null = null;

  get componentInstance(): ComponentRef<any> | null {
    return this._componentInstance;
  }

  private viewContainerRef = inject(ViewContainerRef);
  private applicationRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private remoteModuleLoaderService = inject(MmuRemoteModuleService);

  static createModuleIdentifier(config: MmuRemoteComponentConfig): string {
    const tmp = [config.remoteModule, config.moduleFile, config.moduleClass];
    return tmp.join('__');
  }

  static validateRemoteComponentConfig(config: MmuRemoteComponentConfig): boolean {
    return !(!config || !config.componentClass || !config.moduleFile || !config.remoteModule);
  }

  ngOnInit(): Promise<void> {
    if (!MmuRemoteComponentRenderer.validateRemoteComponentConfig(this._config)) {
      console.error(
        'MmuRemoteComponentRenderer: Insufficient configuration to render the remote component. Please double-check the ' +
        'configuration input: ',
        this._config
      );
      return Promise.reject();
    }

    return this.renderComponent();
  }

  async renderComponent(): Promise<void> {
    try {
      this.viewContainerRef.clear();

      // Get the (JavaScript-) module from the remote server
      const module = await this.remoteModuleLoaderService.loadRemoteModule(this._config);

      // Create a module instance of the Angular module using the current injector as a parent
      const ngModuleType: Type<unknown> | undefined = this._config.moduleClass ? module[this._config.moduleClass] : undefined;
      const ngComponentType: Type<unknown> | undefined = module[this._config.componentClass];

      if (!ngComponentType) {
        console.error(`MmuRemoteComponentRenderer: Component class ${this._config.componentClass} not found within the remote module! Check your configuration. Do you have a typo in the component name?`);
        return;
      }

      const ngModuleRef = ngModuleType ? createNgModule(ngModuleType, this.injector) : undefined;
      const environmentInjector = this.applicationRef.injector;

      // Render and add the component to this directive's view container. It can either an ngModuleRef OR an environmentInjector be passed.
      this._componentInstance = this.viewContainerRef.createComponent(ngComponentType, {
        injector: ngModuleRef?.injector ?? this.injector,
        environmentInjector: ngModuleRef ?? environmentInjector
      });

      this.componentLoaded.emit(this._componentInstance);
    } catch (e) {
      console.error(e);
    }
  }
}
